import { useCallback, useEffect, useState } from "react"
import { fetchImageBase64, IMAGE_TYPES } from "monitor/Configurator/service"
import { toImageDataUrl, getCachedImage, setCachedImage } from "utils/remoteImageCache"

const BRAND_LOGO_CACHE_KEY = "app.brandLogo.dataUrl"
const BRAND_LOGO_CACHE_TS_KEY = "app.brandLogo.ts"
const BRAND_LOGO_CACHE_TTL_MS = 1000 * 60 * 30 // 30 min

function useBrandLogo(fallbackLogo) {
  const [brandLogo, setBrandLogo] = useState(() => {
    const cached = getCachedImage({
      key: BRAND_LOGO_CACHE_KEY,
      tsKey: BRAND_LOGO_CACHE_TS_KEY,
      ttlMs: BRAND_LOGO_CACHE_TTL_MS,
    })
    return cached || fallbackLogo
  })

  const fetchAndCacheBrandLogo = useCallback(async () => {
    try {
      const companyId = (sessionStorage.getItem("companyId") || localStorage.getItem("companyId") || "").trim()
      if (!companyId) {
        setBrandLogo(fallbackLogo)
        return
      }
      const payload = await fetchImageBase64(IMAGE_TYPES.BRAND, companyId)
      const nextLogo = toImageDataUrl(payload)

      setCachedImage({
        key: BRAND_LOGO_CACHE_KEY,
        tsKey: BRAND_LOGO_CACHE_TS_KEY,
        value: nextLogo,
      })
      setBrandLogo(nextLogo || fallbackLogo)
    } catch {
      setBrandLogo(fallbackLogo)
    }
  }, [fallbackLogo])

  useEffect(() => {
    const cached = getCachedImage({
      key: BRAND_LOGO_CACHE_KEY,
      tsKey: BRAND_LOGO_CACHE_TS_KEY,
      ttlMs: BRAND_LOGO_CACHE_TTL_MS,
    })

    if (cached !== undefined) {
      setBrandLogo(cached || fallbackLogo)
      return
    }

    fetchAndCacheBrandLogo()
  }, [fetchAndCacheBrandLogo, fallbackLogo])

  useEffect(() => {
    const handleBrandRefresh = () => {
      fetchAndCacheBrandLogo()
    }

    window.addEventListener("brand-logo:refresh", handleBrandRefresh)
    return () => {
      window.removeEventListener("brand-logo:refresh", handleBrandRefresh)
    }
  }, [fetchAndCacheBrandLogo])

  return brandLogo
}

export default useBrandLogo
